import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography, keyframes } from '@mui/material';
import { styled } from '@mui/material/styles';
import BrandWordmark, { CHECK_NAVY, NOW_GRADIENT_TOP, NOW_GRADIENT_BOTTOM } from './BrandWordmark';

// Next 14:30 (2:30 PM) – today or tomorrow
const getLaunchTime = (): Date => {
  const now = new Date();
  const launch = new Date(now);
  launch.setHours(14, 30, 0, 0);
  if (now >= launch) launch.setDate(launch.getDate() + 1);
  return launch;
};

// Shield Logo Component (matching Login page)
const ShieldLogo = () => (
  <svg width="120" height="120" viewBox="0 0 120 120" style={{ filter: 'drop-shadow(0px 2px 5px rgba(0,0,0,0.2))' }}>
    <path d="M60 10L10 30V55C10 80.7 30 104.5 60 110C90 104.5 110 80.7 110 55V30L60 10Z" fill="none" stroke="#222222" strokeWidth="5"/>
    <path d="M60 10L10 30V55C10 80.7 30 104.5 60 110V10Z" fill="#dd2c00"/> {/* Red */}
    <path d="M60 10L110 30V55C110 80.7 90 104.5 60 110V10Z" fill="#1e88e5"/> {/* Blue */}
    <path d="M10 55C10 80.7 30 104.5 60 110V55H10Z" fill="#2e7d32"/> {/* Green */}
    <path d="M60 55H110C110 80.7 90 104.5 60 110V55Z" fill="#fbc02d"/> {/* Yellow */}
    <path d="M42 60L55 75L80 45" fill="none" stroke="white" strokeWidth="8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// Animation keyframes
const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const rotateReverse = keyframes`
  from {
    transform: rotate(360deg);
  }
  to {
    transform: rotate(0deg);
  }
`;

const gradient = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.02);
    opacity: 0.95;
  }
`;

const countdownPop = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
`;

// Styled components – brand blue theme to match Check-Now
const Overlay = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 9999,
  background: `linear-gradient(-45deg, ${NOW_GRADIENT_TOP}, ${NOW_GRADIENT_BOTTOM}, ${CHECK_NAVY}, ${NOW_GRADIENT_BOTTOM})`,
  backgroundSize: '400% 400%',
  animation: `${gradient} 15s ease infinite`,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
}));

const BackgroundCog = styled(Box)<{ 
  size: number; 
  top?: string; 
  bottom?: string;
  left?: string; 
  right?: string;
  delay: number; 
  reverse?: boolean 
}>(({ size, top, bottom, left, right, delay, reverse }) => ({
  position: 'absolute',
  width: `${size}px`,
  height: `${size}px`,
  ...(top && { top }),
  ...(bottom && { bottom }),
  ...(left && { left }),
  ...(right && { right }),
  opacity: 0.1,
  animation: `${reverse ? rotateReverse : rotate} ${20 + delay * 2}s linear infinite`,
  animationDelay: `${delay}s`,
  '& svg': {
    width: '100%',
    height: '100%',
    fill: '#ffffff',
  },
}));

const LogoContainer = styled(Box)({
  position: 'relative',
  marginBottom: '2rem',
  zIndex: 1,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  filter: 'drop-shadow(0 10px 30px rgba(255, 255, 255, 0.3))',
});

const AnimatedShield = styled(Box)({
  '& svg': {
    width: '120px',
    height: '120px',
  },
});

const ContentBox = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  zIndex: 1,
  position: 'relative',
  padding: '2rem 2.5rem',
  borderRadius: '24px',
  background: `linear-gradient(180deg, ${NOW_GRADIENT_BOTTOM} 0%, ${CHECK_NAVY} 100%)`,
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255,255,255,0.08)',
}));

const Subtitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.25rem',
  color: 'rgba(255, 255, 255, 0.92)',
  fontWeight: 400,
  fontFamily: 'Lato, sans-serif',
  letterSpacing: '1px',
}));

const LiveLaunchTitle = styled(Typography)({
  fontSize: '3.5rem',
  fontWeight: 900,
  fontStyle: 'italic',
  color: '#ffffff',
  marginBottom: '0.25rem',
  fontFamily: "'Montserrat', sans-serif",
  letterSpacing: '4px',
  textShadow: '0 2px 12px rgba(0, 0, 0, 0.2)',
  animation: `${pulse} 2s ease-in-out infinite`,
});

const CountdownBox = styled(Box)({
  display: 'flex',
  gap: '1rem',
  justifyContent: 'center',
  flexWrap: 'wrap',
  marginTop: '1.5rem',
  marginBottom: '1rem',
});

const CountdownUnit = styled(Box)({
  minWidth: '4.5rem',
  padding: '0.75rem 1rem',
  borderRadius: '12px',
  background: 'rgba(255, 255, 255, 0.15)',
  border: '1px solid rgba(255, 255, 255, 0.25)',
  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
  animation: `${countdownPop} 1.5s ease-in-out infinite`,
  '&:nth-of-type(1)': { animationDelay: '0s' },
  '&:nth-of-type(2)': { animationDelay: '0.2s' },
  '&:nth-of-type(3)': { animationDelay: '0.4s' },
  '&:nth-of-type(4)': { animationDelay: '0.6s' },
});

const CountdownValue = styled(Typography)({
  fontSize: '2.25rem',
  fontWeight: 800,
  color: '#ffffff',
  fontFamily: "'Montserrat', sans-serif",
  lineHeight: 1.2,
});

const CountdownLabel = styled(Typography)({
  fontSize: '0.7rem',
  color: 'rgba(255, 255, 255, 0.85)',
  fontWeight: 600,
  fontFamily: 'Lato, sans-serif',
  letterSpacing: '1.5px',
  textTransform: 'uppercase',
  marginTop: '0.25rem',
});

const CogIcon: React.FC<{ size?: number }> = ({ size = 100 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12 15.5A3.5 3.5 0 0 1 8.5 12A3.5 3.5 0 0 1 12 8.5A3.5 3.5 0 0 1 15.5 12A3.5 3.5 0 0 1 12 15.5M19.43 12.97C19.47 12.65 19.5 12.33 19.5 12C19.5 11.67 19.47 11.34 19.43 11.03L21.54 9.37C21.73 9.22 21.78 8.95 21.66 8.73L19.66 5.27C19.54 5.05 19.27 4.96 19.05 5.05L16.56 6.05C16.04 5.66 15.5 5.32 14.87 5.07L14.5 2.42C14.46 2.18 14.25 2 14 2H10C9.75 2 9.54 2.18 9.5 2.42L9.13 5.07C8.5 5.32 7.96 5.66 7.44 6.05L4.95 5.05C4.73 4.96 4.46 5.05 4.34 5.27L2.34 8.73C2.22 8.95 2.27 9.22 2.46 9.37L4.57 11.03C4.53 11.34 4.5 11.67 4.5 12C4.5 12.33 4.53 12.65 4.57 12.97L2.46 14.63C2.27 14.78 2.22 15.05 2.34 15.27L4.34 18.73C4.46 18.95 4.73 19.03 4.95 18.95L7.44 17.94C7.96 18.34 8.5 18.68 9.13 18.93L9.5 21.58C9.54 21.82 9.75 22 10 22H14C14.25 22 14.46 21.82 14.5 21.58L14.87 18.93C15.5 18.68 16.04 18.34 16.56 17.94L19.05 18.95C19.27 19.03 19.54 18.95 19.66 18.73L21.66 15.27C21.78 15.05 21.73 14.78 21.54 14.63L19.43 12.97Z"
      fill="currentColor"
    />
  </svg>
);

const MaintenanceOverlay: React.FC = () => {
  const launchTime = useMemo(() => getLaunchTime(), []);
  const [diff, setDiff] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    const tick = () => {
      const now = new Date();
      const ms = Math.max(0, launchTime.getTime() - now.getTime());
      setDiff({
        days: Math.floor(ms / (24 * 60 * 60 * 1000)),
        hours: Math.floor((ms % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000)),
        minutes: Math.floor((ms % (60 * 60 * 1000)) / (60 * 1000)),
        seconds: Math.floor((ms % (60 * 1000)) / 1000),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [launchTime]);

  return (
    <Overlay>
      {/* Background animated cogs */}
      <BackgroundCog size={200} top="10%" left="5%" delay={0}>
        <CogIcon size={200} />
      </BackgroundCog>
      <BackgroundCog size={150} top="20%" right="10%" delay={2} reverse>
        <CogIcon size={150} />
      </BackgroundCog>
      <BackgroundCog size={180} top="60%" left="8%" delay={4} reverse>
        <CogIcon size={180} />
      </BackgroundCog>
      <BackgroundCog size={120} top="70%" right="15%" delay={1}>
        <CogIcon size={120} />
      </BackgroundCog>
      <BackgroundCog size={160} top="40%" right="5%" delay={3}>
        <CogIcon size={160} />
      </BackgroundCog>
      <BackgroundCog size={140} bottom="10%" left="15%" delay={5} reverse>
        <CogIcon size={140} />
      </BackgroundCog>
      <BackgroundCog size={100} bottom="15%" right="8%" delay={2.5}>
        <CogIcon size={100} />
      </BackgroundCog>

      {/* Main content */}
      <LogoContainer>
        <AnimatedShield>
          <ShieldLogo />
        </AnimatedShield>
      </LogoContainer>

      <ContentBox>
        <Box sx={{ mb: 1.5 }}>
          <BrandWordmark variant="h2" />
        </Box>
        <LiveLaunchTitle variant="h1">
          LIVE LAUNCH
        </LiveLaunchTitle>
        <Subtitle variant="h6">
          We're going live at 2:30 PM
        </Subtitle>
        <CountdownBox>
          <CountdownUnit>
            <CountdownValue>{String(diff.days).padStart(2, '0')}</CountdownValue>
            <CountdownLabel>Days</CountdownLabel>
          </CountdownUnit>
          <CountdownUnit>
            <CountdownValue>{String(diff.hours).padStart(2, '0')}</CountdownValue>
            <CountdownLabel>Hours</CountdownLabel>
          </CountdownUnit>
          <CountdownUnit>
            <CountdownValue>{String(diff.minutes).padStart(2, '0')}</CountdownValue>
            <CountdownLabel>Minutes</CountdownLabel>
          </CountdownUnit>
          <CountdownUnit>
            <CountdownValue>{String(diff.seconds).padStart(2, '0')}</CountdownValue>
            <CountdownLabel>Seconds</CountdownLabel>
          </CountdownUnit>
        </CountdownBox>
      </ContentBox>
    </Overlay>
  );
};

export default MaintenanceOverlay;

